import {
    startMonthOptions,
    experienceNeededOptions,
    targetGroupOptions,
    themeOptions,
} from 'utilities/filterOptions';

/**
 * Filter trainings by search value
 * @param {Array} trainings - Array of training objects
 * @param {string} searchValue - Search string
 * @returns {Array} Filtered trainings
 */
export const filterBySearch = (trainings, searchValue) => {
    if (!searchValue.length) return trainings;

    return trainings.filter((training) =>
        training?.name?.toLowerCase().includes(searchValue.toLowerCase())
    );
};

/**
 * Filter trainings by target group
 * @param {Array} trainings - Array of training objects
 * @param {Array} targetGroupSelected - Selected target groups
 * @returns {Array} Filtered trainings
 */
export const filterByTargetGroup = (trainings, targetGroupSelected) => {
    if (!targetGroupSelected.length) return trainings;

    return trainings.filter((training) =>
        targetGroupSelected.find((group) => {
            const trainingArr =
                training.target_groups &&
                Object.entries(training.target_groups)
                    .map((g) => (g[1] ? g[0] : undefined))
                    .filter((exists) => exists);

            const shortGroup = targetGroupOptions?.find(
                (option) => option.long === group
            )?.short;

            return trainingArr?.includes(shortGroup);
        })
    );
};

/**
 * Filter trainings by experience level
 * @param {Array} trainings - Array of training objects
 * @param {Array} experienceSelected - Selected experience levels
 * @returns {Array} Filtered trainings
 */
export const filterByExperience = (trainings, experienceSelected) => {
    if (!experienceSelected.length) return trainings;

    return trainings.filter((training) =>
        experienceSelected.find((group) => {
            const trainingArr =
                training.experience_needed &&
                Object.entries(training.experience_needed)
                    .map((g) => (g[1] ? g[0] : undefined))
                    .filter((exists) => exists);

            const shortGroup = experienceNeededOptions?.find(
                (option) => option.long === group
            )?.short;

            return trainingArr?.includes(shortGroup);
        })
    );
};

/**
 * Filter trainings by theme
 * @param {Array} trainings - Array of training objects
 * @param {Array} themeSelected - Selected themes
 * @returns {Array} Filtered trainings
 */
export const filterByTheme = (trainings, themeSelected) => {
    if (!themeSelected.length) return trainings;

    return trainings.filter((training) =>
        themeSelected.find((group) => {
            const themeArr =
                training.themes &&
                Object.entries(training.themes)
                    .map((g) => (g[1] ? g[0] : undefined))
                    .filter((exists) => exists);

            const shortGroup = themeOptions?.find(
                (option) => option.long === group
            )?.short;

            return themeArr?.includes(shortGroup);
        })
    );
};

/**
 * Filter trainings by start month
 * @param {Array} trainings - Array of training objects
 * @param {Array} startMonthSelected - Selected start months
 * @returns {Array} Filtered trainings
 */
export const filterByStartMonth = (trainings, startMonthSelected) => {
    if (!startMonthSelected.length) return trainings;

    return trainings.filter((training) => {
        if (training.start_date && training.enrollment_open) {
            const date = new Date(training?.start_date);
            const month = date.getMonth() + 1; // getMonth is zero-based
            return startMonthSelected.includes(
                startMonthOptions.find((e) => e.short === month)?.long
            );
        }
        return startMonthSelected.includes('Nader te bepalen');
    });
};

/**
 * Filter trainings by school type (school vs general trainings)
 * @param {Array} trainings - Array of training objects
 * @param {boolean} isSchoolsTrainingPage - Whether viewing school-specific trainings
 * @param {Object} school - School object
 * @param {Object} schoolGroup - School group object
 * @returns {Array} Filtered trainings
 */
export const filterBySchoolType = (
    trainings,
    isSchoolsTrainingPage,
    school,
    schoolGroup
) =>
    trainings.filter((training) => {
        if (!isSchoolsTrainingPage) {
            return !training.schoolable_id;
        }

        return training.schoolable_type === 'App\\Models\\School'
            ? training.schoolable_id === school?.id
            : training.schoolable_id === schoolGroup?.id;
    });

/**
 * Sort trainings by order column
 * @param {Array} trainings - Array of training objects
 * @returns {Array} Sorted trainings
 */
export const sortTrainings = (trainings) =>
    [...trainings].sort((a, b) => a.order_column - b.order_column);
